import { useEffect, useState } from 'react';
import client from '../../../lib/feathers';

/**
 * "Tell us what happens here."
 *
 * Deliberately not a category picker. Asking a winery to classify itself against
 * our 60-entry taxonomy is a chore that produces bad data — on production 2,949
 * of 3,096 listings carry exactly one category, almost always "Winery", so the
 * field tells a visitor almost nothing. These are plain questions about the
 * address; the server derives the categories.
 *
 * The second group is always available rather than gated on the first three
 * being "no" — an estate with a restaurant is common, and gating would make that
 * unreachable.
 */

const PRIMARY = [
  { key: 'tastings', question: 'Do you pour tastings for visitors here?' },
  { key: 'makesWine', question: 'Do you make wine here?' },
  { key: 'growsGrapes', question: 'Do you grow grapes here?' },
];

// Each of these turns on the same axis: does the offer involve OTHER people's
// wine or places? Without that they would be true of nearly every winery.
const SECONDARY = [
  { key: 'sellsOtherProducers', question: 'Do you sell bottles from other producers here?' },
  { key: 'poursOtherProducers', question: "Do you pour other producers' wine by the glass?" },
  { key: 'servesFood', question: 'Do you serve food?' },
  { key: 'toursToOthers', question: 'Do you take guests to other wineries?' },
];

const ALL = [...PRIMARY, ...SECONDARY];

function YesNo({ value, onChange }) {
  const base = 'px-4 py-1.5 text-sm font-medium border first:rounded-l-md last:rounded-r-md -ml-px first:ml-0';
  return (
    <div className="flex shrink-0">
      {[['Yes', true], ['No', false]].map(([label, v]) => (
        <button
          key={label}
          type="button"
          onClick={() => onChange(v)}
          className={`${base} ${
            value === v
              ? 'bg-red-800 border-red-800 text-white'
              : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
          }`}
        >
          {label}
        </button>
      ))}
    </div>
  );
}

export default function ProfileSurveyModal({ listing, onClose, onSaved }) {
  const [answers, setAnswers] = useState({});
  const [amenities, setAmenities] = useState([]);
  const [chosen, setChosen] = useState(new Set());
  const [showMore, setShowMore] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await client.service('industry/accommodation').find({
          query: { $limit: 200, $sort: { type: 1, name: 1 } },
        });
        if (!cancelled) setAmenities(res.data || res || []);
      } catch (e) {
        // The survey still works without amenities; don't block on them.
        console.log('could not load amenities', e);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Seed from what the listing already claims, so this reads as confirming
  // rather than starting from nothing.
  useEffect(() => {
    const names = (listing?.categories || []).map((c) => c.name);
    const seeded = {};
    if (names.includes('Tasting Room')) seeded.tastings = true;
    if (names.includes('Winery')) seeded.makesWine = true;
    if (names.includes('Vineyard')) seeded.growsGrapes = true;
    if (names.includes('Wine Shop')) seeded.sellsOtherProducers = true;
    if (names.includes('Wine Bar')) seeded.poursOtherProducers = true;
    if (names.includes('Restaurant')) seeded.servesFood = true;
    if (names.includes('Tour Company')) seeded.toursToOthers = true;
    setAnswers(seeded);

    const existing = (listing?.accommodations || []).map((a) => a.id);
    if (existing.length) setChosen(new Set(existing));
  }, [listing?.id]);

  const answered = ALL.filter((q) => answers[q.key] !== undefined).length;

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      // Send every question, not just the yeses — an unanswered question must
      // never be read as a "no", or the first save would strip a listing's only
      // category.
      const activities = {};
      for (const q of ALL) activities[q.key] = Boolean(answers[q.key]);

      await client.service('industry/listing-profile-answers').create({
        listingId: listing.id,
        activities,
        amenities: [...chosen],
      });
      onSaved?.();
      onClose?.();
    } catch (e) {
      setError(e.message || 'Could not save your answers.');
    } finally {
      setSaving(false);
    }
  };

  const grouped = amenities.reduce((acc, a) => {
    (acc[a.type || 'other'] = acc[a.type || 'other'] || []).push(a);
    return acc;
  }, {});

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="fixed inset-0 bg-gray-900 bg-opacity-50" onClick={onClose} />
      <div className="relative mx-auto my-10 max-w-2xl bg-white rounded-lg shadow-xl">
        <div className="px-6 py-5 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Tell us what happens here</h3>
          <p className="mt-1 text-sm text-gray-500">
            This is how visitors find you. It takes about a minute.
          </p>
        </div>

        <div className="px-6 py-5 space-y-4 max-h-[60vh] overflow-y-auto">
          {PRIMARY.map((q) => (
            <div key={q.key} className="flex items-center justify-between gap-4">
              <span className="text-sm text-gray-800">{q.question}</span>
              <YesNo value={answers[q.key]} onChange={(v) => setAnswers({ ...answers, [q.key]: v })} />
            </div>
          ))}

          <div className="pt-2">
            <button
              type="button"
              onClick={() => setShowMore(!showMore)}
              className="text-sm text-red-800 hover:underline"
            >
              {showMore ? 'Hide' : 'Anything else at this location?'}
            </button>
          </div>

          {showMore && (
            <div className="space-y-4 pt-1">
              {SECONDARY.map((q) => (
                <div key={q.key} className="flex items-center justify-between gap-4">
                  <span className="text-sm text-gray-800">{q.question}</span>
                  <YesNo value={answers[q.key]} onChange={(v) => setAnswers({ ...answers, [q.key]: v })} />
                </div>
              ))}
            </div>
          )}

          {amenities.length > 0 && (
            <div className="pt-4 border-t border-gray-200">
              <h4 className="text-sm font-medium text-gray-900">What should visitors know?</h4>
              <p className="mt-1 text-xs text-gray-500">
                These are the questions people ask before they drive out to you.
              </p>
              <div className="mt-3 space-y-3">
                {Object.entries(grouped).map(([type, list]) => (
                  <div key={type}>
                    <div className="text-xs uppercase tracking-wide text-gray-400 mb-1.5">{type}</div>
                    <div className="flex flex-wrap gap-2">
                      {list.map((a) => {
                        const on = chosen.has(a.id);
                        return (
                          <button
                            key={a.id}
                            type="button"
                            onClick={() => {
                              const next = new Set(chosen);
                              if (on) next.delete(a.id); else next.add(a.id);
                              setChosen(next);
                            }}
                            className={`px-3 py-1 text-sm rounded-full border ${
                              on
                                ? 'bg-red-800 border-red-800 text-white'
                                : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                            }`}
                          >
                            {a.name}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {error && <p className="text-sm text-red-600">{error}</p>}
        </div>

        <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
          <span className="text-sm text-gray-500">{answered} of {ALL.length} answered</span>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={save}
              disabled={saving || answered === 0}
              className="px-4 py-2 text-sm rounded-md text-white bg-red-800 hover:bg-red-900 disabled:opacity-50"
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
